import {compact, countBy, Dictionary, every, filter, first, forEach, groupBy, map, max, min, nth, size, some,
  sortBy, transform, uniq, intersection, keys, flatten, values} from 'lodash';
import {action, computed, makeObservable, observable} from 'mobx';
import {onEnterKeyHandler} from 'apstra-ui-common';

import {pickBatchChangeFor, pickPortChannelFromRange, registerNodeChange, sameValue, speedFromString,
  tryPlacing} from '../utils';
import {mlagKeepaliveVlanId, NODE_ROLES, PEER_LINKS_TYPES, PORT_ROLES, REDUNDANCY} from '../const';
import {OptionType} from '../components/LinksGroupForm';

import Node, {NodePropertiesType} from './Node';
import Step from '../../cablingMapEditor/store/Step';
import RackEditorStore from './RackEditorStore';
import LinksGroup from './LinksGroup';

class NodePropertiesStore {
  @observable.ref rackEditorStore: RackEditorStore;
  @observable result!: Node;
  @observable nodesInvolved: Node[] = []; // Not the same as selection.selectedNodes

  @observable portChannelId: number = 0;
  @observable portChannelIdExt: number = 0;

  @computed
  get firstNode(): Node | null {
    return first(this.nodesInvolved) || null;
  }

  @computed
  get selectionSize(): number {
    return size(this.nodesInvolved);
  }

  @computed
  get isPair(): boolean {
    return this.selectionSize === 2 && !!this.firstNode?.isPairedWith(nth(this.nodesInvolved, 1)!.id);
  }

  @computed
  get isBatch(): boolean {
    return this.selectionSize > 1 && !this.isPair;
  }

  @computed
  get isSwitch(): boolean {
    return this.result.role && !this.result.isGeneric;
  }

  @computed
  get noLogicalDevice(): boolean {
    return !this.result.logicalDevice;
  }

  @computed
  get portChannels(): number[] {
    return compact(flatten(map(this.nodesInvolved, 'myPortChannelIds')));
  }

  @computed
  get portChannelsNeeded(): number {
    return max(
      values(
        transform(
          this.nodesInvolved,
          (acc, {myLagLinksGroups}) => {
            forEach(myLagLinksGroups, ({toNode}) => {
              acc[toNode!.label] = (acc[toNode!.label] ?? 0) + 1;
            });
          },
          {}
        )
      )
    ) || 0;
  }

  // True if all selected generics have portChannelId set
  @computed
  get allPortChannelsSet() {
    return !some(this.nodesInvolved, 'hasEnoughPortChannelIds');
  }

  @computed
  get batchSpineLinksSpeed(): string | null {
    return sameValue(this.nodesInvolved, 'spineLinksSpeed', null);
  }

  // Related links groups by peering type
  @computed
  get linksByPeeringType(): Record<string, LinksGroup[]> {
    const {rackStore: {linksGroups}} = this.rackEditorStore;
    return groupBy(
      filter(linksGroups, (linksGroup) => linksGroup.contains(this.nodesInvolved, false)),
      'peeringType'
    );
  }

  @computed
  get haveRails(): boolean {
    const {rackStore} = this.rackEditorStore;
    return some(this.nodesInvolved, ({id}: Node) => rackStore.leafsWithRails[id]);
  }

  // Discover which redundant pairs given node is connected with
  @computed
  get hasRedundantNonPeerLinks(): Dictionary<boolean> {
    const {nodesByName} = this.rackEditorStore.rackStore;

    return transform(
      this.linksByPeeringType?.null,
      (acc, {fromName, toName, isPeer}) => {
        if (!isPeer) {
          const redundancy = first(nodesByName[this.result.name === fromName ? toName : fromName])?.redundancyProtocol;
          if (redundancy) acc[redundancy] = true;
        }
      },
      {}
    );
  }

  @computed
  get hasLinksToESI(): boolean {
    return this.hasRedundantNonPeerLinks[REDUNDANCY.ESI];
  }

  @computed
  get hasLinksToMLAG(): boolean {
    return this.hasRedundantNonPeerLinks[REDUNDANCY.MLAG];
  }

  // Make MLAG redundancy available only if:
  @computed
  get canSwitchToMlag(): boolean {
    const {rackStore} = this.rackEditorStore;

    // Role is leaf
    return this.result.isLeaf &&
      // Logical Device is defined
      !this.noLogicalDevice &&
      // Node has no links to ESI access switch pairs
      !this.hasLinksToESI && (
      // There are no ESI leaf pairs in rack type
      !rackStore.esiLeafPairsCount || (
        // or the only ESI leaf pair is the current node
        rackStore.esiLeafPairsCount === 1 && this.result.isEsiLeafPair
      ));
  }

  // Make ESI redundancy available only if:
  @computed
  get canSwitchToEsi(): boolean {
    const {rackStore} = this.rackEditorStore;

    // Logical Device is defined
    return !this.noLogicalDevice &&
    // Node has no links to MLAG leaf pairs
    !this.hasLinksToMLAG && (
      // Role is access switch
      !this.result.isLeaf || (
        // There are no MLAG leaf pairs in rack type
        !rackStore.mlagLeafPairsCount || (
          // or the only MLAG leaf pair is the current node
          rackStore.mlagLeafPairsCount === 1 && this.result.isMlagLeafPair
        )
      )
    );
  }

  // Affected nodes include selected nodes + their pairs
  @computed
  get affectedNodes(): Node[] {
    const {rackStore} = this.rackEditorStore;
    return this.isBatch ?
      sortBy(
        compact(
          map(
            uniq(
              transform(
                this.nodesInvolved,
                (acc: string[], {idsAffected}) => acc.push(...idsAffected),
                []
              )
            ),
            (id) => rackStore.nodes[id]
          )
        ),
        'id'
      ) :
      [this.firstNode!];
  }

  // Ports used by each of affected nodes
  @computed
  get hostedPorts() {
    return this.batchSpineLinksSpeed ?
      map(this.affectedNodes, 'usedPorts') :
      map(this.affectedNodes, nonSpinePortsFilter);
  }

  @computed
  get spinePorts(): Dictionary<number> {
    if (!this.result.isLeaf) return {};

    // For batch management all selected nodes must have available SPINE ports of the same speed
    const allSpinePorts = transform(
      this.affectedNodes,
      (acc: Dictionary<number[]>, {availablePorts}) => {
        const nodeSpinePorts = countBy(filter(availablePorts, {hasSpineRole: true}), 'speedString');
        forEach(nodeSpinePorts, (count, speedString) => {
          (acc[speedString] ??= []).push(count);
        });
      },
      {}
    );

    const affectedCount = size(this.affectedNodes);
    return transform(
      allSpinePorts,
      (acc, speedCounts, speedString) => {
        if (size(speedCounts) !== affectedCount) return;
        acc[speedString] = min(speedCounts);
      },
      {}
    );
  }

  @computed
  get maxSpineLinks(): number {
    if (!this.result.spineLinksSpeed) return 0;
    return (this.spinePorts?.[this.result.spineLinksSpeed] ?? 0) + (this.result.spineLinksCount || 0);
  }

  @computed
  get spinePortsExist(): boolean {
    return size(this.spinePorts) > 0 || this.result.spineLinksCount > 0;
  }

  @computed
  get deviceOptions(): OptionType[] {
    const {rackStore} = this.rackEditorStore;
    const deviceFilter = devicesFilters[this.result.role];
    return sortBy(
      map(
        filter(
          rackStore.devices,
          ({id, ports}) => {
            // First try to filted LDs out by the role (e.g. Access Switch should always have a
            // connection to Leaf, thus it needs ports with 'leaf' role).
            // If nodes of different roles selected - role filter is empty
            const roleFilter =
            // Filter in already selected device or
              id === this.result.logicalDevice?.id ||
              // that correspond to the node's role
              some(ports, (port) => port.matchesAnyOfRoles(deviceFilter(rackStore.isL3Clos)));
            if (!roleFilter) return false;

            // Check if this LD is capable of hosting ALL links for each of selected nodes
            // including their pairs
            return every(this.hostedPorts, (nodePorts) => !!tryPlacing(nodePorts, ports));
          }
        ),
        ({id: value, display_name: text}) => ({text, value})
      ), 'value'
    );
  }

  @computed
  get allDevicesAreVisible(): boolean {
    const {rackStore} = this.rackEditorStore;
    return size(this.deviceOptions) === size(rackStore.devices);
  }

  @computed
  get portChannelError(): boolean {
    return this.isBatch ?
      some(this.nodesInvolved, 'hasPortChannelErrors') :
      !!this.result.portChannelIdsErrors?.portChannelId;
  }

  @computed
  get portChannelRangeError(): boolean {
    return (
      // One of the range values is 0, while the other is defined
      ((this.portChannelId || this.portChannelIdExt) && (!this.portChannelId || !this.portChannelIdExt)) ||
      // or the range is negative
      (this.portChannelId > this.portChannelIdExt)
    );
  }

  // TRUE if all selected nodes are paired with MLAG redundancy
  @computed
  get allMlagPairs(): boolean {
    return sameValue(this.nodesInvolved, 'isMlagLeafPair', false);
  }

  constructor(rackEditorStore: RackEditorStore, nodes: Node[]) {
    makeObservable(this);

    this.rackEditorStore = rackEditorStore;
    this.init(nodes);
  }

  @computed
  get nodesSorted() {
    return sortBy(this.nodesInvolved, ({portChannelIdsNeeded}) => -portChannelIdsNeeded);
  }

  @action
  init(nodes: Node[] = []) {
    this.nodesInvolved = nodes;
    if (!this.selectionSize) throw new Error('No nodes selected');
    this.result = this.isBatch ?
      new Node({
        label: '',
        logicalDevice: sameValue(nodes, 'logicalDevice', null, 'logicalDevice.id'),
        tags: sameValue(nodes, 'tags', []),
        portChannelId: min(this.portChannels),
        portChannelIdExt: max(this.portChannels),
        mlagVlanId: sameValue(nodes, 'mlagVlanId', mlagKeepaliveVlanId),
        spineLinksSpeed: this.batchSpineLinksSpeed,
        spineLinksCount: this.batchSpineLinksSpeed ? sameValue(nodes, 'spineLinksCount', 0) : 0,
        role: sameValue(nodes, 'role')
      }, this.rackEditorStore.rackStore) :
      this.firstNode!;

    this.portChannelId = this.result.portChannelId || 0;
    this.portChannelIdExt = this.result.portChannelIdExt || 0;
  }

  @action
  trackChange = (property: NodePropertiesType, value: unknown, applyToPair?: boolean): void => {
    const {rackStore} = this.rackEditorStore;

    if (this.isBatch) {
      this.result.setProperty(property, value, applyToPair);
    }
    const change = pickBatchChangeFor(this.affectedNodes, rackStore);
    registerNodeChange(change, rackStore, property, value);
  };

  @action
  setPortChannel = (minValue: number, maxValue: number) => {
    this.portChannelId = minValue;
    this.portChannelIdExt = maxValue;

    if (this.isBatch) {
      const {rackStore} = this.rackEditorStore;

      // Find/generate batch change
      const change = pickBatchChangeFor(this.affectedNodes, rackStore);

      forEach(
        this.nodesSorted,
        (node) => {
          if (this.portChannelRangeError) {
            // if range is invalid - reset the node's PCIDs
            node.fillWith({
              portChannelId: 0,
              portChannelIdExt: 0
            });
          } else {
            // Pick the PCID from the range otherwise
            pickPortChannelFromRange(node, this.portChannelId, this.portChannelIdExt);
          }
        }
      );
      // Register changes made to affected nodes
      registerNodeChange(change, rackStore);
    } else {
      // Reset the node's PCIDs if the range is invalid
      this.trackChange('portChannelId', this.portChannelRangeError ? 0 : minValue);
      this.trackChange('portChannelIdExt', this.portChannelRangeError ? 0 : maxValue);
    }
  };

  @action
  changeRedundancy = (option) => {
    if (this.isBatch) return;
    const {value} = option;

    if (this.result.redundancyProtocol !== value) {
      const change: Step[] = [];

      const {rackStore, selection} = this.rackEditorStore;
      if (!value) {
        // If removing redundancy - just delete the pair node with their links

        // Delete pair node
        const pairId = this.result.pairedWith?.id;
        if (pairId) {
          selection.remove(pairId);
          // Adding node+links deletion to the change
          rackStore.deleteNode(pairId, change);
        }
      } else {
        const step = Step.modification(this.result).inverted;
        if (this.result.redundancyProtocol) {
          // Redundancy protocol change ESI<->MLAG

          // Every redundancy change must remove peer links
          forEach(
            this.result.isLeaf ?
              // ... and L3 peers for ESI leaf pairs
              compact([
                ...this.linksByPeeringType?.[PEER_LINKS_TYPES.L2_PEER] ?? [],
                ...this.linksByPeeringType?.[PEER_LINKS_TYPES.L3_PEER] ?? []
              ]) :
              this.linksByPeeringType[PEER_LINKS_TYPES.L2_PEER],
            (linksGroup) => {
              // Register deletion in history
              change.push(Step.deletion(linksGroup));
              // Delete links group
              rackStore.deleteLinksGroups([linksGroup.id]);
            }
          );
        } else {
          // Create peer node

          const [cloneId] = rackStore.cloneSelected([this.result.id], true, false);
          if (cloneId) {
            const clone = rackStore.nodes[cloneId];
            rackStore.createPeerNodeFor(this.result, value, clone);

            // Adding clone node creation to the change
            change.push(Step.creation(clone));
          }
        }

        // Set new redundancy protocol (will be propagated to the pair node)
        this.result.redundancyProtocol = value;

        // Attempt to create peer links when mandatory
        if (this.result.isLeaf && value === REDUNDANCY.MLAG) {
          // L2 peer link is mandatory for MLAG leaf pairs
          const peerPortsSpeeds = intersection(
            ...map(this.result.nodesInvolved, ({availablePorts}) => {
              return map(filter(availablePorts, (port) => port.matchesRole(PORT_ROLES.PEER)), 'speedString');
            })
          );
          if (peerPortsSpeeds.length > 0) {
            // Peer link can be created (both nodes has peer ports of the same speed)
            // Create the peer link
            const peerLink = LinksGroup.createPeerLinks(
              this.result.name,
              speedFromString(peerPortsSpeeds[0]),
              1,
              rackStore,
              PEER_LINKS_TYPES.L2_PEER
            );
            // Add it to the store
            rackStore.addLinksGroup(peerLink);
            // And to the changeset
            change.push(Step.creation(peerLink));
          }
        }

        step.setResult(this.result);
        change.push(step);
      }

      // Registering change in the history
      rackStore.changes.register(change);
    }
  };

  @action
  changeLogicalDevice = (logicalDevice: any, prevSpineLinksSpeed: string | null) => {
    const {rackStore} = this.rackEditorStore;
    // We are assuming that for already defined spine links only LDs with existing ports of the same
    // speed will be supplied (such LD filtering is done in deviceOptions getter)

    // Changing logical device
    this.trackChange('logicalDevice', logicalDevice);

    // Check if it is possible to set "Links per spine" immediately
    if (this.result.isLeaf && rackStore.isL3Clos && this.spinePortsExist) {
      const speedString = prevSpineLinksSpeed && !!this.spinePorts[prevSpineLinksSpeed] ?
        prevSpineLinksSpeed :
        first(keys(this.spinePorts));
      this.trackChange('spineLinksCount', 1);
      this.trackChange('spineLinksSpeed', speedString);
    }
  };

  closeOnEnter = onEnterKeyHandler(() => this.rackEditorStore.rackStore.toggleOptions(true));
}

export default NodePropertiesStore;

const nonSpinePortsFilter = ({usedPorts}) => filter(usedPorts, {hasSpineRole: false});

const devicesFilters = {
  [NODE_ROLES.LEAF]: (isL3Clos) => (
    // If design is L3Clos, device is supposed to have port with spine roles available
    isL3Clos ? [PORT_ROLES.SPINE] : []
  ),
  [NODE_ROLES.ACCESS_SWITCH]: () => (
    // Access switches should always be connected with leafs so having ports with leaf role is
    // mandatory
    [PORT_ROLES.LEAF]
  ),
  [NODE_ROLES.GENERIC]: () => (
    // Any generic has to be either connected with a leaf or access switch (or both)
    [PORT_ROLES.LEAF, PORT_ROLES.ACCESS_SWITCH]
  ),
  undefined: () => []
};
